import { useEffect, useMemo, useState } from "react";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { SortingState } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import type { UserFilters } from "@/api/users";
import { useUsers } from "@/hooks";
import { usePagination } from "@/hooks/usePagination";
import { useNavigateModal } from "@/router/useNavigateModal";
import type { User } from "@/types";
import { Pagination, Table } from "@/ui";
import { getTableColumns } from "./UsersTableColumns";

export const UsersTable = ({
  searchQuery,
  locked,
  filters,
}: {
  searchQuery: string;
  locked: number | string;
  filters?: UserFilters;
}) => {
  const navigate = useNavigate();
  const navigateModal = useNavigateModal();
  const { pagination, setPageIndex, onPaginationChange } = usePagination();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { useAllUsers } = useUsers();

  const firstSort = sorting[0];
  const sortParam = firstSort
    ? firstSort.desc
      ? `-${firstSort.id}`
      : firstSort.id
    : undefined;

  const { data: usersData, isLoading } = useAllUsers(
    pagination.pageIndex + 1,
    searchQuery,
    locked,
    filters,
    sortParam,
  );

  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery, locked, filters, sorting, setPageIndex]);

  const columns = useMemo(
    () => getTableColumns(navigateModal),
    [navigateModal],
  );

  const table = useReactTable({
    data: usersData?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: usersData?.pagination?.totalPages ?? 1,
    state: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: usersData?.pagination?.perPage ?? 10,
      },
      sorting,
    },
    onPaginationChange,
    onSortingChange: setSorting,
  });

  const handleRowClick = (user: User) => {
    navigate(`/users/${user.id}`);
  };

  return (
    <div>
      <Table
        table={table}
        handleRowClick={handleRowClick}
        isLoading={isLoading}
        rowClassName={(user) =>
          user.locked ? "bg-error-50 hover:bg-error-100" : undefined
        }
      />
      <div className="flex items-center justify-between gap-3 rounded-b-2xl border border-neutral-200 px-4">
        <span className="text-sm text-neutral-500">
          {usersData?.pagination?.total ?? 0}{" "}
          {usersData?.pagination?.total === 1 ? "user" : "users"}
        </span>
        <Pagination
          currentPage={pagination.pageIndex + 1}
          totalPages={usersData?.pagination?.totalPages ?? 1}
          onPageChange={(page) => setPageIndex(page - 1)}
        />
      </div>
    </div>
  );
};
